import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";

import { useState, useEffect, memo, useCallback, useRef } from "react";

import { toast } from "react-toastify";
import * as riki from "jsriki";
import store from "store2";

import { storyFilterBoxState, activeLabelsState } from "./JotaiAtoms";

import { newStoriesState } from "./JotaiAtoms";
import { useAtomValue, useSetAtom } from "jotai";

import { FileUploadPreview } from "./FileUploadPreview";
import { StoryLabels } from "./StoryLabels";
import * as u from "./utility";
import {
  handleFilesFromPasteEvent,
  MAX_MESSAGE_SIZE,
  TextFieldWithSuggestions,
  StoryRef,
  getFilesFromDataTransfer,
  processUploadedFiles,
  SuccessfulUpload,
  cacheUploadedFiles,
  clearInputWithMathHighlights,
  MathState,
} from "./SendMessageBox";

import { useHttpPostAndProject, useProjectCache, useUploadFile } from "./hooks";
import { DummyStory } from "./StoryItem";
import { FileUpload } from "./MessageItem";
import { preparseTextWithMath } from "./Markdown";

type CreateErrorToastProps = {
  onRetry: (e: React.MouseEvent<HTMLElement>) => void;
};

function CreateErrorToast({ onRetry }: CreateErrorToastProps) {
  return (
    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} width="100%">
      <Typography sx={{ fontSize: '13px', color: 'var(--text-primary)' }}>
        Failed to create a new story
      </Typography>
      <Button
        onClick={onRetry}
        size="small"
        sx={{
          textTransform: 'none',
          fontSize: '12px',
          fontWeight: 500,
          px: 1.5,
          py: 0.5,
          minWidth: 'auto',
          color: 'var(--accent-primary)',
          '&:hover': {
            backgroundColor: 'var(--surface-hover)',
          },
        }}
      >
        Retry
      </Button>
    </Stack>
  );
}

const makeFileKey = (file: File) => {
  return `${file.name}:${file.size}`;
};

function findSolution(expr: string): string[] {
  const solution = riki.find_solution(expr);
  if (solution && solution.length) {
    return solution;
  }

  let labels = [];
  const operands = Array.from(new Set(riki.extract_expression_operands(expr)));

  let done = false;
  for (const op of operands) {
    if (riki.evaluate_expression(expr, op)) {
      labels.push(op);
      done = true;
      break;
    }
  }

  for (let i = 0; i < operands.length - 1 && !done; ++i) {
    for (let j = i; j < operands.length && !done; ++j) {
      const op1 = operands[i];
      const op2 = operands[j];
      if (riki.evaluate_expression(expr, `${op1} ${op2}`)) {
        labels.push(op1);
        labels.push(op2);
        done = true;
        break;
      }
    }
  }

  if (!done && riki.evaluate_expression(expr, operands.join(" "))) {
    labels.push(...operands);
  }

  return labels;
}

type ComposeAreaProps = {
  open?: boolean;
  drawerWidth?: number;
};

export function ComposeArea({ open, drawerWidth = 0 }: ComposeAreaProps) {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<boolean>(() => store.get('ComposeAreaCollapsed', false));
  const [input, setInput] = useState<boolean>(false);
  const [text, updateCachedText, setText] = useProjectCache("ComposeArea", "");
  const [chips, setChips] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<FileUpload[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Map<string, string>>(
    new Map()
  );
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);
  const [refs, setRefs] = useState<StoryRef[]>([]);

  const filters = useAtomValue(storyFilterBoxState);
  const [droppedFiles, setDroppedFiles] = useState<File[]>();
  const setNewStories = useSetAtom(newStoriesState);
  const [httpPost, project] = useHttpPostAndProject();
  const uploadToS3 = useUploadFile();
  const inputRef = useRef<HTMLInputElement>();
  const [allowCreate, setAllowCreate] = useState(false);
  const activeLabels = useAtomValue(activeLabelsState);

  // Auto-focus input when expanding from collapsed state
  useEffect(() => {
    if (!collapsed && inputRef.current) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [collapsed]);

  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    store.set('ComposeAreaCollapsed', newState);
  };

  const getProjectFilters = (): string[] => {
    return filters && filters[project] ? filters[project] : [];
  };

  const resetChipsBasedOnCurrentSelection = () => {
    const filters = getProjectFilters().filter((e) => !e.startsWith("@"));
    const expressions = filters.filter((e) => u.isLabelExpression(e));
    const plainLabels = filters.filter((e) => !u.isLabelExpression(e));

    let joinExpression = "";

    for (const filter of filters) {
      if (joinExpression) {
        joinExpression += " or ";
      }
      if (u.isLabelExpression(filter)) {
        joinExpression += " ( " + filter + " ) ";
      } else {
        joinExpression += filter;
      }
    }

    if (activeLabels) {
      for (let i = 0; i < 3; ++i) {
        const queries = Object.values(activeLabels).filter(
          (e) => e.expression && e.name
        );
        let successCount = 0;
        for (const q of queries) {
          const replaced = joinExpression.replaceAll(
            ` ${q.name!} `,
            ` ( ${q.expression!} ) `
          );
          if (replaced != joinExpression) {
            successCount += 1;
          }
          joinExpression = replaced;
        }

        if (successCount == 0) {
          break;
        }
      }
    }

    // example: ( ( (:white_check_mark: or cantreproduce or wontdo or nothingtodo ) ) )  or todo
    const newChips: string[] = [];
    if (joinExpression) {
      const solution = findSolution(joinExpression);
      if (activeLabels) {
        newChips.push(
          ...solution.filter((e) => {
            return !activeLabels[e]?.expression;
          })
        );
      } else {
        newChips.push(...solution);
      }
    }
    newChips.push(...plainLabels);
    setChips(Array.from(new Set(newChips)));
  };

  useEffect(() => {}, [chips]);

  useEffect(() => {
    if (filters) {
      resetChipsBasedOnCurrentSelection();
    }
  }, [filters]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.value = text || "";
    }
    updateAllowCreate(text);
  }, [text]);

  useEffect(() => {
    const value: string = inputRef?.current?.value || "";
    updateAllowCreate(value);
  }, [selectedFiles]);

  const handleKey = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    } else if (e.key === "Escape") {
      cancelWebStory();
    }
  };

  const handleDescChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setText(value);
    updateCachedText(value);
    updateAllowCreate(value);
  };

  const cancelWebStory = () => {
    setText("");
    setInput(false);

    resetChipsBasedOnCurrentSelection();

    setSelectedFiles([]);
    setUploadedFiles(new Map());
    setRefs([]);
    updateCachedText("");
  };

  const handleChange = (newChips: string[]) => {
    setChips(newChips);
  };

  const handleCreate = () => {
    if (!inputRef.current) {
      return;
    }

    const mathState: MathState = (inputRef.current as any).mathState;
    const math = mathState ? [...mathState.exprBounds] : undefined;

    const desc = inputRef.current.value;

    const request = async (dummy: DummyStory) => {
      type FileObj = {
        uri: string;
        type: string;
        size: number;
        name: string;
      };

      try {
        const uploaded: FileObj[] = [];
        for (let i = 0; i < selectedFiles.length; ++i) {
          const f = selectedFiles[i];
          const key = makeFileKey(f.original);
          let uri: string | undefined = uploadedFiles.get(key);
          if (!uri) {
            uri = (await uploadToS3(f.original)) || undefined;
            if (uri) {
              cacheUploadedFiles(project, [{ uri: uri, file: f }]);
            }
          }

          if (!uri) {
            return false;
          }

          uploaded.push({
            uri: uri,
            type: f.original.type,
            size: f.original.size,
            name: f.original.name,
          });
        }

        type RequestObj = {
          new: true;
          desc: string;
          label: string[];
          files: FileObj[];
          refs: StoryRef[];
          math?: { start: number; end: number }[];
        };

        const requestObj: RequestObj = {
          new: true,
          desc: desc,
          label: chips,
          files: uploaded,
          refs: refs,
        };

        if (math && math.length > 0) {
          requestObj.math = math;
        }

        const response = await httpPost("stories", requestObj);

        if (response.data.new.length) {
          let story = response.data.new[0];
          story["old_dummy_message"] = dummy;
          story.internal_update = true;

          setNewStories((prev) => [...prev, story]);

          // update the story in hopes to get the autodetected st_ui from images
          if (uploaded.length > 0) {
            setTimeout(() => {
              httpPost("stories", { stories: true, story: story.SK }).then(
                (response) => {
                  let story = response.data.stories[0];
                  story["old_dummy_message"] = dummy;
                  story.internal_update = true;
                  setNewStories((prev) => [...prev, story]);
                }
              );
            }, 2000);
          }
        }

        return true;
      } catch (err) {}

      return false;
    };

    if (!desc.length && !selectedFiles.length) {
      return;
    }

    const ts = u.getCurrentTimestamp();

    const dummy: DummyStory = {
      PK: "",
      SK: ts,
      SK2: ts,
      SK3: ts,
      st_desc: desc,
      st_pp: preparseTextWithMath(desc, math || []),
      st_labels: chips,
      uploaded_files: selectedFiles,
      refs: refs,
      dummy: true,
      ts: ts,
    };

    setNewStories((prev) => [...prev, dummy]);

    // Clear everything first
    setText("");
    updateCachedText("");
    setSelectedFiles([]);
    setUploadedFiles(new Map());
    setRefs([]);
    resetChipsBasedOnCurrentSelection();
    clearInputWithMathHighlights(inputRef.current);

    // Keep focus on input for quick consecutive entries
    setInput(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);

    const attemptCreate = async () => {
      setNewStories((prev) => {
        let found = prev.findIndex(
          (m) => dummy.SK === m.SK && (m as DummyStory).dummy
        );
        if (found >= 0) {
          let out = [...prev];
          out[found] = { ...dummy, error: undefined };
          return out;
        }

        return [
          ...prev,
          {
            ...dummy,
            error: null,
          },
        ];
      });

      const ok = await request(dummy);
      if (ok) {
        return;
      }

      setNewStories((prev) => {
        let found = prev.findIndex(
          (m) => dummy.SK === m.SK && (m as DummyStory).dummy
        );
        if (found >= 0) {
          let out = [...prev];
          out[found] = {
            ...dummy,
            error: undefined,
            old_dummy_message: { SK: dummy.SK },
          };
          return out;
        }

        return [
          ...prev,
          {
            ...dummy,
            old_dummy_message: { SK: dummy.SK },
            error: "create error",
          },
        ];
      });

      toast.error(<CreateErrorToast onRetry={attemptCreate} />);
    };

    attemptCreate();
  };

  const addFilesToStory = (files: FileList | File[]) => {
    const silentUpload = async (newFiles: FileUpload[]) => {
      let newUploaded = new Map(uploadedFiles);
      let successfulUploads: SuccessfulUpload[] = [];
      for (const f of newFiles) {
        // this may fail, but we do not do any error handling
        const cacheKey = await uploadToS3(f.original);
        const fileKey = makeFileKey(f.original);
        if (cacheKey) {
          newUploaded.set(fileKey, cacheKey);
          successfulUploads.push({ uri: cacheKey, file: f });
        }
      }
      setUploadedFiles(newUploaded);

      cacheUploadedFiles(project, successfulUploads);
    };

    processUploadedFiles(files, selectedFiles, setSelectedFiles, silentUpload);
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFilesToStory(e.target.files);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragActive(false);
    getFilesFromDataTransfer(e.dataTransfer, setDroppedFiles);
    setInput(true);
  };

  useEffect(() => {
    if (droppedFiles) {
      addFilesToStory(droppedFiles);
      setDroppedFiles(undefined);
    }
  }, [droppedFiles]);

  const handleDelete = useCallback(
    (index: number) => {
      setSelectedFiles((prev) => {
        let next = [...prev];
        next.splice(index, 1);
        return next;
      });
    },
    [setSelectedFiles]
  );

  const handleBlur = () => {
    if (inputRef?.current) {
      updateCachedText(inputRef.current.value);
    }
  };

  useEffect(() => {
    if (input && inputRef?.current) {
      inputRef.current.value = text;
    }
  }, [input]);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    handleFilesFromPasteEvent(e, addFilesToStory, setRefs);
  };

  const updateAllowCreate = (inputValue: string) => {
    setAllowCreate(inputValue.length > 0 || selectedFiles.length > 0);
  };

  if (collapsed) {
    return (
      <Stack
        direction="row"
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.mode === 'dark' ? '#1f1f1f' : '#f8fafb',
          borderTop: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 -4px 12px rgba(0, 0, 0, 0.3)'
            : '0 -4px 12px rgba(0, 0, 0, 0.06)',
          py: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
          }}
        >
          <IconButton
            onClick={toggleCollapsed}
            size="small"
            sx={{
              color: 'var(--text-tertiary)',
              '&:hover': {
                color: 'var(--text-secondary)',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <ExpandLessIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        position: 'relative',
        backgroundColor: theme.palette.mode === 'dark' ? '#1f1f1f' : '#f8fafb',
        borderTop: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 -4px 12px rgba(0, 0, 0, 0.3)'
          : '0 -4px 12px rgba(0, 0, 0, 0.06)',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          py: 2.5,
          px: 4,
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
      {/* Drag overlay */}
      {dragActive && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(138, 180, 248, 0.08)'
              : 'rgba(25, 103, 210, 0.08)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            animation: 'fadeIn 0.2s ease',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: 4,
              borderRadius: '12px',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(31, 31, 31, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              border: '2px dashed var(--accent-primary)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Box
              sx={{
                fontSize: '48px',
                lineHeight: 1,
              }}
            >
              📎
            </Box>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'var(--text-primary)',
              }}
            >
              Drop files here to attach
            </Typography>
          </Box>
        </Box>
      )}
      {/* Horizontal layout: input+files on left, labels on right */}
      <Stack direction="row" alignItems="flex-start" gap={2}>
        {/* Main container - split vertically */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            sx={{
              borderRadius: '12px',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.12)'
                : '1px solid rgba(0, 0, 0, 0.12)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 1px 3px rgba(0, 0, 0, 0.3)'
                : '0 1px 3px rgba(0, 0, 0, 0.1)',
              minHeight: '88px',
              maxHeight: '40vh',
              overflow: 'hidden',
              backgroundColor: theme.palette.mode === 'dark' ? '#1f1f1f' : '#ffffff',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.2)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 6px rgba(0, 0, 0, 0.4)'
                  : '0 2px 6px rgba(0, 0, 0, 0.15)',
              },
              '&:focus-within': {
                borderColor: 'var(--accent-primary)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 0 0 2px rgba(138, 180, 248, 0.2), 0 2px 6px rgba(0, 0, 0, 0.4)'
                  : '0 0 0 2px rgba(25, 103, 210, 0.1), 0 2px 6px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            {/* Left side: input + files (same background, no divider) */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'transparent',
              borderTopLeftRadius: '12px',
              borderBottomLeftRadius: '12px',
              minWidth: 0,
              position: 'relative',
            }}>
              {/* Input area - scrollable */}
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                px: 2,
                pt: 1.5,
                pb: 1.5,
                pr: '54px',
                minHeight: 0,
                // Webkit scrollbar styling
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.25)',
                },
                // Firefox scrollbar styling
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0, 0, 0, 0.15) transparent',
              }}>
                <TextFieldWithSuggestions
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  variant="outlined"
                  size="medium"
                  placeholder="Share a story, paste a link, drop files..."
                  onBlur={handleBlur}
                  onKeyDown={handleKey}
                  onChange={handleDescChange}
                  onPaste={handlePaste}
                  onFocus={() => setInput(true)}
                  inputProps={{ maxLength: MAX_MESSAGE_SIZE }}
                  flipped={true}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      padding: 0,
                      '& fieldset': {
                        border: 'none',
                      },
                      '& textarea': {
                        padding: 0,
                      },
                    },
                  }}
                />
              </Box>

              {/* File previews - always visible at bottom, not scrollable */}
              {selectedFiles.length > 0 && (
                <Box
                  sx={{
                    px: 2,
                    pb: 1.5,
                    flexShrink: 0,
                    '& img': {
                      maxHeight: '80px',
                      maxWidth: '120px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                    },
                  }}
                >
                  <MemoFileUploadPreview
                    files={selectedFiles.map((e) => e.preview || e.original)}
                    onDelete={handleDelete}
                    readOnly
                    fontSize={9}
                  />
                </Box>
              )}

              {/* Clip button - fixed at bottom right */}
              <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 10 }}>
                <input
                  type="file"
                  id={`new-web-story-file-upload`}
                  hidden
                  onChange={handleFileSelected}
                  multiple
                />
                <label htmlFor={`new-web-story-file-upload`}>
                  <IconButton
                    component="span"
                    size="medium"
                    sx={{
                      color: 'var(--text-secondary)',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        color: 'var(--accent-primary)',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </label>
              </Box>
            </Box>

            {/* Right side: labels panel - full height */}
            {allowCreate && (
              <Box
                sx={{
                  borderLeft: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderTopRightRadius: '12px',
                  borderBottomRightRadius: '12px',
                  minWidth: '200px',
                  maxWidth: '300px',
                  flexShrink: 0,
                  px: 2,
                  pb: 1,
                  pt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'auto',
                  // Webkit scrollbar styling
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0, 0, 0, 0.15) transparent',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    alignContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    marginTop: 'auto',
                    '& > *': {
                      display: 'inline-flex',
                      alignItems: 'center',
                    }
                  }}
                >
                  <StoryLabels
                    labels={chips}
                    setLabels={setChips}
                    onLabelCreated={handleChange}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Action buttons on the right */}
        {allowCreate && (
          <Stack direction="row" gap={1.5} sx={{ flexShrink: 0, pt: 0.5 }}>
            <Button
              variant="text"
              size="medium"
              onClick={() => cancelWebStory()}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                color: 'var(--text-secondary)',
                minWidth: 'auto',
                px: 2,
                py: 1,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!allowCreate}
              variant="contained"
              size="medium"
              onClick={handleCreate}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                backgroundColor: 'var(--accent-primary)',
                color: '#ffffff',
                minWidth: 'auto',
                px: 3,
                py: 1,
                borderRadius: '8px',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 4px rgba(138, 180, 248, 0.3)'
                  : '0 2px 4px rgba(25, 103, 210, 0.2)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? '#6fa0ff'
                    : '#1565c0',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 8px rgba(138, 180, 248, 0.4)'
                    : '0 4px 8px rgba(25, 103, 210, 0.3)',
                },
                '&:disabled': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.05)',
                  color: 'var(--text-tertiary)',
                  boxShadow: 'none',
                },
              }}
            >
              Create
            </Button>
          </Stack>
        )}
      </Stack>
      </Box>
      {/* Collapse button on the right */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
        }}
      >
        <IconButton
          onClick={toggleCollapsed}
          size="small"
          sx={{
            color: 'var(--text-tertiary)',
            '&:hover': {
              color: 'var(--text-secondary)',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Stack>
  );
}

const MemoFileUploadPreview = memo(FileUploadPreview);
